import { useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api.js';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { StatusBadge, PublicationPip, formatDateLong, LIFECYCLE_LABEL } from '../lib/events.jsx';
import { LoadingState, ErrorState } from '../lib/Spinner.jsx';
import { DateTimeField, DateField, TimeField, formatEventTime, formatStamp, isoToZonedInput, zonedInputToIso } from '../lib/timeFields.jsx';
import { FreshnessBadge } from '../lib/metaFreshness.jsx';
import { useVenueTz } from '@/lib/venueTz.js';
import EventDrawer from './EventDrawer.jsx';

// Labels come from the canonical LIFECYCLE_LABEL map so the editor's status
// dropdown reads the same words ("Ready / Queued / Live") as the list/calendar
// badges — one vocabulary everywhere. (DB enum values unchanged.)
const STATUS_OPTIONS = [
  { value: 'draft',            label: LIFECYCLE_LABEL.draft },
  { value: 'sized_up',         label: LIFECYCLE_LABEL.sized_up },
  { value: 'pending_announce', label: LIFECYCLE_LABEL.pending_announce },
  { value: 'announced',        label: LIFECYCLE_LABEL.announced },
  { value: 'cancelled',        label: LIFECYCLE_LABEL.cancelled },
];

function PriceCents({ cents }) {
  if (cents == null) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

// ISO date string (YYYY-MM-DD) → input[type=date] value
function dateInput(iso) {
  if (!iso) return '';
  return new Date(iso).toISOString().slice(0, 10);
}

// ISO TIME (HH:MM:SS) → input[type=time] value
function timeInput(t) {
  if (!t) return '';
  return String(t).slice(0, 5); // HH:MM
}

export default function EventDetail() {
  const { tenantSlug, eventId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const editingVersionId = searchParams.get('version');  // null = edit canonical (working draft)
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState(null);
  const [tierDraft, setTierDraft] = useState(null);
  const [stamping, setStamping] = useState(false);
  const [tierError, setTierError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [publishResults, setPublishResults] = useState(null);
  const [publishError, setPublishError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const refresh = () => {
    setLoading(true);
    setError(null);
    apiFetch(`/api/tenants/${tenantSlug}/events/${eventId}`)
      .then((r) => r.json())
      .then((d) => {
        if (d.error) throw new Error(d.error);
        setEvent(d);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(refresh, [tenantSlug, eventId]); // eslint-disable-line

  const publishAllDefaults = async () => {
    if (!confirm('Push this event to every default platform now? Each platform runs independently; partial failures are fine.')) return;
    setPublishing(true);
    setPublishError(null);
    setPublishResults(null);
    try {
      const res = await apiFetch(`/api/tenants/${tenantSlug}/events/${eventId}/publish-all-defaults`, { method: 'POST' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || d.error || `http ${res.status}`);
      setPublishResults(d);
      refresh(); // pull updated publication rows
    } catch (e) {
      setPublishError(e.message);
    } finally {
      setPublishing(false);
    }
  };

  const stampFromTemplate = async (force = false) => {
    setStamping(true);
    setTierError(null);
    try {
      const url = `/api/tenants/${tenantSlug}/events/${eventId}/stamp-from-template${force ? '?force=1' : ''}`;
      const res = await apiFetch(url, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.error);
      refresh();
    } catch (e) {
      setTierError(e.message);
    } finally {
      setStamping(false);
    }
  };

  const saveTiers = async () => {
    setTierError(null);
    try {
      const body = {
        tiers: tierDraft.map((t, i) => ({
          name: t.name,
          gender: t.gender || null,
          release_step: t.release_step === '' ? null : Number(t.release_step),
          price_cents: Number(t.price_cents),
          capacity: t.capacity === '' ? null : Number(t.capacity),
          sort_index: i,
        })),
      };
      const res = await apiFetch(`/api/tenants/${tenantSlug}/events/${eventId}/tiers`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error + (data.detail ? `: ${data.detail}` : ''));
      setTierDraft(null);
      refresh();
    } catch (e) {
      setTierError(e.message);
    }
  };

  const startTierEdit = () => {
    setTierError(null);
    setTierDraft(event.tiers.map((t) => ({
      name: t.name,
      gender: t.gender || '',
      release_step: t.release_step ?? '',
      price_cents: t.price_cents,
      capacity: t.capacity ?? '',
    })));
  };

  const addTier = () => setTierDraft((d) => [...d, { name: 'New tier', gender: '', release_step: '', price_cents: 0, capacity: '' }]);
  const removeTier = (i) => setTierDraft((d) => d.filter((_, idx) => idx !== i));
  const updateTier = (i, field, value) => setTierDraft((d) => d.map((t, idx) => idx === i ? { ...t, [field]: value } : t));

  const uploadMedia = async (file, kind, mediaId = null) => {
    setUploading(true);
    setUploadError(null);
    try {
      const isVideo = file.type.startsWith('video/');
      const cap = isVideo ? 200 * 1024 * 1024 : 5 * 1024 * 1024;
      if (file.size > cap) throw new Error(`File over ${isVideo ? '200' : '5'} MB limit`);
      const fd = new FormData();
      fd.append('file', file);
      const qs = new URLSearchParams({ kind });
      if (mediaId) qs.set('media_id', mediaId);
      const res = await apiFetch(`/api/tenants/${tenantSlug}/events/${eventId}/media?${qs}`, {
        method: 'PUT',
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.error);
      refresh();
    } catch (e) {
      setUploadError(e.message);
    } finally {
      setUploading(false);
    }
  };

  const deleteMedia = async (mediaId) => {
    if (!confirm('Remove this media?')) return;
    try {
      const res = await apiFetch(`/api/tenants/${tenantSlug}/events/${eventId}/media/${mediaId}`, { method: 'DELETE' });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'delete failed');
      }
      refresh();
    } catch (e) {
      setUploadError(e.message);
    }
  };

  const enterEdit = () => {
    // Editing a specific version → source its snapshot; else the canonical event (working draft).
    const ev = (event.versions || []).find((v) => String(v.id) === String(editingVersionId));
    const src = ev?.snapshot || event;
    setDraft({
      talent: src.talent || '',
      event_name: src.event_name || '',
      headliner: src.headliner || '',
      lineup: src.lineup || '',
      status: event.status || 'draft',
      ref_code: event.ref_code || '',
      internal_notes: event.internal_notes || '',
      short_description: src.short_description || '',
      description_html: src.description_html || '',
      announce_date: dateInput(src.announce_date),
      announce_time: timeInput(src.announce_time),
      age_limit: src.age_limit ?? '',
      doors_at: isoToZonedInput(src.doors_at, event.venue_timezone),
      end_at: isoToZonedInput(src.end_at, event.venue_timezone),
    });
    setSaveError(null);
    setEditing(true);
  };
  // Reload the draft when switching which version is being edited (while in edit mode);
  // and auto-open the editor when arrived via ?edit=1 (e.g. chevron "Edit").
  useEffect(() => {
    if (event && editing) enterEdit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingVersionId]);
  useEffect(() => {
    if (event && !editing && searchParams.get('edit') === '1') enterEdit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);

  const cancelEdit = () => {
    setDraft(null);
    setEditing(false);
    setSaveError(null);
  };

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const body = { ...draft };
      // age_limit string → number or null
      if (body.age_limit === '') body.age_limit = null;
      else body.age_limit = Number(body.age_limit);
      // datetime-local wall clock is in the VENUE timezone → convert to an
      // instant using the venue tz (not the viewer's browser tz).
      if (body.doors_at) body.doors_at = zonedInputToIso(body.doors_at, event.venue_timezone);
      if (body.end_at) body.end_at = zonedInputToIso(body.end_at, event.venue_timezone);

      // A selected version → save to that version's snapshot; else the canonical event.
      const url = editingVersionId
        ? `/api/tenants/${tenantSlug}/events/${eventId}/versions/${editingVersionId}`
        : `/api/tenants/${tenantSlug}/events/${eventId}`;
      const res = await apiFetch(url, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error + (data.detail ? `: ${data.detail}` : ''));
      setEditing(false);
      setDraft(null);
      refresh();
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const setField = (k) => (e) => {
    const v = e.target ? e.target.value : e;
    setDraft((d) => ({ ...d, [k]: v }));
  };

  const generate = async () => {
    setGenerating(true);
    setGenError(null);
    try {
      const res = await apiFetch(`/api/tenants/${tenantSlug}/events/${eventId}/generate-description`, {
        method: 'POST',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.error);
      // Populate the draft with the generated content (ops can still edit before save)
      setDraft((d) => ({
        ...(d || {
          talent: event.talent || '',
          headliner: event.headliner || '',
          lineup: event.lineup || '',
          status: event.status || 'draft',
          ref_code: event.ref_code || '',
          internal_notes: event.internal_notes || '',
          announce_date: dateInput(event.announce_date),
          announce_time: timeInput(event.announce_time),
          age_limit: event.age_limit ?? '',
          doors_at: isoToZonedInput(event.doors_at, event.venue_timezone),
          end_at: isoToZonedInput(event.end_at, event.venue_timezone),
        }),
        description_html: data.description_html,
        short_description: data.short_description,
      }));
      if (!editing) setEditing(true);
    } catch (e) {
      setGenError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  if (loading) return <LoadingState label="Loading event…" />;
  if (error) return <ErrorState error={error} onRetry={() => window.location.reload()} />;
  if (!event) return null;

  return (
    <div className="max-w-5xl">
      <div className="mb-4 flex items-center justify-between">
        <Link to={`/${tenantSlug}/list`} className="text-sm text-muted-foreground hover:text-foreground">← Back to list</Link>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setDrawerOpen(true)}
            className="px-2 py-1.5 text-sm border border-input rounded-md hover:bg-accent"
            title="Quick view: status, drift, and push/announce actions"
          >Quick view</button>
          {!editing && (
            <button
              onClick={async () => {
                if (!confirm(`Delete "${event.talent}"? It can be restored from Recently Deleted for 30 days.`)) return;
                const res = await apiFetch(`/api/tenants/${tenantSlug}/events/${eventId}`, { method: 'DELETE' });
                if (res.ok) navigate(`/${tenantSlug}/list`);
              }}
              className="px-2 py-1.5 text-sm border border-destructive text-destructive rounded-md hover:bg-destructive/10"
            >Delete</button>
          )}
          {editing ? (
            <>
              {saveError && <span className="text-sm text-destructive">{saveError}</span>}
              <button
                onClick={cancelEdit}
                disabled={saving}
                className="px-3 py-1.5 text-sm border border-input rounded-md hover:bg-accent disabled:opacity-50"
              >Cancel</button>
              <button
                onClick={save}
                disabled={saving}
                className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:opacity-90 disabled:opacity-50"
              >{saving ? 'Saving…' : 'Save'}</button>
            </>
          ) : (
            <>
              <SyncPlatformsButton
                tenantSlug={tenantSlug}
                eventId={eventId}
                event={event}
                onSynced={refresh}
              />
              <AnnounceButton
                tenantSlug={tenantSlug}
                eventId={eventId}
                event={event}
                onAnnounced={refresh}
              />
              <button
                onClick={enterEdit}
                className="px-3 py-1.5 text-sm border border-input rounded-md hover:bg-accent"
              >Edit</button>
            </>
          )}
        </div>
      </div>

      {/* Drift banner, visible top-of-page when Universe state diverges
          from canonical. Drift panel inside Universe section is the
          detailed compare; this nudges ops to go look. */}
      {event.has_drift && (
        <div className="mb-3 px-3 py-2 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 text-sm flex items-center justify-between">
          <span>● Universe state has drifted from canonical</span>
          <Link to={`/${tenantSlug}/event/${eventId}/universe#drift`} className="text-xs hover:underline font-medium">Resolve drift →</Link>
        </div>
      )}

      {/* Banner header, full-width above the title. Renders only when set;
          upload control is in the media gallery below. */}
      {event.banner_image_url && (
        <div className="mb-4 rounded-md overflow-hidden border border-border bg-muted">
          <img src={event.banner_image_url} alt="" className="w-full max-h-64 object-cover" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: media column */}
        <div className="space-y-4">
          <MediaSlot
            label="Flyer (square)"
            kind="flyer"
            url={event.flyer_image_url}
            accept="image/jpeg,image/png,image/webp"
            uploading={uploading}
            uploadError={uploadError}
            onUpload={(f) => uploadMedia(f, 'flyer')}
          />
          <MediaSlot
            label="Banner (header)"
            kind="banner"
            url={event.banner_image_url}
            accept="image/jpeg,image/png,image/webp"
            aspect="aspect-[16/9]"
            uploading={uploading}
            onUpload={(f) => uploadMedia(f, 'banner')}
          />
          <MediaSlot
            label="Cover"
            kind="cover"
            url={event.cover_image_url}
            accept="image/jpeg,image/png,image/webp"
            uploading={uploading}
            onUpload={(f) => uploadMedia(f, 'cover')}
          />

          {/* Additional media (anything that isn't a primary flyer/banner/cover). */}
          <AdditionalMediaGallery
            media={event.media || []}
            uploading={uploading}
            onUpload={uploadMedia}
            onDelete={deleteMedia}
          />
        </div>

        <div className="lg:col-span-2 space-y-6">
          {/* Header */}
          <div>
            {/* Version bar, visible in BOTH view and edit mode so ops can always
                see which version this is and pick another. Selecting a version
                opens it for editing (read-only view of a historical snapshot is
                not wired, picking = open it). Universe title/description/attraction
                follow these versions; ticket tiers + settings are live (template + edits). */}
            {(event.versions || []).length > 0 && (
              <div className="mb-3 flex flex-wrap items-center gap-2 text-xs bg-muted/40 rounded-md px-3 py-2">
                <span className="text-muted-foreground">{editing ? 'Editing:' : 'Version:'}</span>
                <select
                  value={editingVersionId || ''}
                  onChange={(e) => setSearchParams(e.target.value ? { version: e.target.value, edit: '1' } : { edit: '1' })}
                  className="px-2 py-1 border border-input rounded-md bg-background"
                >
                  <option value="">Working draft (canonical)</option>
                  {(event.versions || []).slice().sort((a, b) => a.version_number - b.version_number).map((v) => (
                    <option key={v.id} value={v.id}>
                      v{v.version_number} · {v.kind}{v.snapshot?.talent ? ` · ${v.snapshot.talent}` : ''}
                    </option>
                  ))}
                </select>
                <span className="text-muted-foreground">
                  {editing
                    ? (editingVersionId ? "· saving to this version's snapshot" : '· saving to the live event')
                    : `${event.versions.length} version${event.versions.length === 1 ? '' : 's'} · pick to edit`}
                </span>
              </div>
            )}
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                {editing ? (
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">Talent</label>
                    <Input value={draft.talent} onChange={setField('talent')} placeholder="Headline act" className="text-2xl font-semibold" />
                  </div>
                ) : (
                  <h1 className="text-2xl font-semibold">{event.talent}</h1>
                )}
                <div className="text-muted-foreground text-sm mt-1">
                  {formatDateLong(event.date)} · {event.venue_name}
                  {event.genre && <> · {event.genre}</>}
                </div>
              </div>
              {editing ? (
                <select
                  value={draft.status}
                  onChange={setField('status')}
                  className="px-2 py-1 text-sm border border-input rounded-md bg-background"
                >
                  {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : (
                <StatusBadge status={event.status} />
              )}
            </div>
            {event.is_placeholder && (
              <div className="text-xs text-amber-600 mt-1">Placeholder, talent not yet confirmed</div>
            )}
          </div>

          {/* Short description callout (view) or input (edit) */}
          {editing ? (
            <Section title="Short description (social sharing, ~150 chars)">
              <Textarea
                value={draft.short_description}
                onChange={setField('short_description')}
                rows={3}
                maxLength={300}
              />
              <div className="text-xs text-muted-foreground mt-1">{draft.short_description.length} / 300</div>
            </Section>
          ) : event.short_description && event.short_description.length > 30 ? (
            <div className="text-sm bg-muted/50 rounded-md p-3 border-l-2 border-primary">
              {event.short_description}
            </div>
          ) : null}

          {/* Basics */}
          <Section title="Basics">
            <Grid>
              <Field label="Date">{formatDateLong(event.date)}</Field>
              <Field label="Day">{event.day_of_week || '—'}</Field>
              <Field label="Venue">{event.venue_name}</Field>
              <Field label="Headliner" overridden={event.manual_overrides?.headliner}>
                {editing ? (
                  <Input value={draft.headliner} onChange={setField('headliner')} />
                ) : (event.headliner || '—')}
              </Field>
              <Field label="Age limit" overridden={event.manual_overrides?.age_limit}>
                {editing ? (
                  <Input type="number" min="0" max="99" value={draft.age_limit} onChange={setField('age_limit')} />
                ) : (event.age_limit ?? '—')}
              </Field>
              <Field label="Pricing mode">{event.pricing_mode || '—'}</Field>
              <Field label="Doors" overridden={event.manual_overrides?.doors_at}>
                {editing ? (
                  <DateTimeField value={draft.doors_at} tz={event.venue_timezone} onChange={setField('doors_at')}
                    className="w-full px-2 py-1 border border-input rounded-md bg-background text-sm" />
                ) : event.doors_at ? formatEventTime(event.doors_at, event.venue_timezone) : '—'}
              </Field>
              <Field label="End" overridden={event.manual_overrides?.end_at}>
                {editing ? (
                  <DateTimeField value={draft.end_at} tz={event.venue_timezone} onChange={setField('end_at')}
                    className="w-full px-2 py-1 border border-input rounded-md bg-background text-sm" />
                ) : event.end_at ? formatEventTime(event.end_at, event.venue_timezone) : '—'}
              </Field>
              <Field label="Announce date">
                {editing ? (
                  <DateField value={draft.announce_date} onChange={setField('announce_date')}
                    className="w-full px-2 py-1 border border-input rounded-md bg-background text-sm" />
                ) : event.announce_date ? formatDateLong(event.announce_date) : '—'}
              </Field>
              <Field label="Announce time">
                {editing ? (
                  <TimeField value={draft.announce_time} onChange={setField('announce_time')}
                    className="w-full px-2 py-1 border border-input rounded-md bg-background text-sm" />
                ) : (event.announce_time || '—')}
              </Field>
              <Field label="Ref code">
                {editing ? (
                  <Input value={draft.ref_code} onChange={setField('ref_code')} placeholder="ALICE" />
                ) : (event.ref_code ? `?ref=${event.ref_code}` : '—')}
              </Field>
              <Field label="Eventcode (UrVenue)">{event.eventcode || '—'}</Field>
            </Grid>
          </Section>

          {/* Lineup, editable textarea in edit mode (shown even when empty so ops can add one) */}
          {editing ? (
            <Section title="Lineup (one act per line)">
              <Textarea value={draft.lineup} onChange={setField('lineup')} rows={4} placeholder="Headliner&#10;Support act&#10;Opener" />
            </Section>
          ) : event.lineup ? (
            <Section title="Lineup">
              <pre className="whitespace-pre-wrap font-sans text-sm">{event.lineup}</pre>
            </Section>
          ) : null}

          {/* Tiers (editable) */}
          <Section title={`Ticket tiers (${tierDraft ? tierDraft.length : event.tiers.length})`}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs text-muted-foreground">
                {tierDraft ? 'Editing, press Save tiers to commit' : ''}
              </div>
              <div className="flex items-center gap-2">
                {tierError && <span className="text-xs text-destructive">{tierError}</span>}
                {tierDraft ? (
                  <>
                    <button onClick={addTier} className="px-2 py-1 text-xs border border-input rounded-md hover:bg-accent">+ Add tier</button>
                    <button onClick={() => setTierDraft(null)} className="px-2 py-1 text-xs border border-input rounded-md hover:bg-accent">Cancel</button>
                    <button onClick={saveTiers} className="px-2 py-1 text-xs bg-primary text-primary-foreground rounded-md hover:opacity-90">Save tiers</button>
                  </>
                ) : event.tiers.length === 0 ? (
                  <button
                    onClick={() => stampFromTemplate(false)}
                    disabled={stamping}
                    className="px-2 py-1 text-xs border border-input rounded-md hover:bg-accent disabled:opacity-50"
                    title="Copy tier scaffold from this event's matching template (DOW or date-override)"
                  >{stamping ? 'Stamping…' : 'Stamp from template'}</button>
                ) : (
                  <>
                    <button onClick={() => stampFromTemplate(true)} disabled={stamping} className="px-2 py-1 text-xs border border-input rounded-md hover:bg-accent disabled:opacity-50" title="Wipe + re-stamp tiers from template (destructive)">{stamping ? 'Re-stamping…' : 'Re-stamp'}</button>
                    <button onClick={startTierEdit} className="px-2 py-1 text-xs border border-input rounded-md hover:bg-accent">Edit tiers</button>
                  </>
                )}
              </div>
            </div>
            {(tierDraft ? tierDraft.length === 0 : event.tiers.length === 0) ? (
              <div className="text-sm text-muted-foreground">{tierDraft ? 'No tiers. Click "Add tier"' : 'No tiers yet. Click "Stamp from template" to copy the scaffold (or "Edit tiers" → "Add tier" to define them by hand).'}</div>
            ) : (
              <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="py-1">Name</th>
                    <th className="py-1">Gender</th>
                    <th className="py-1">Release</th>
                    <th className="py-1 text-right">Price</th>
                    <th className="py-1 text-right">Capacity</th>
                    {tierDraft && <th className="py-1 w-8"></th>}
                  </tr>
                </thead>
                <tbody>
                  {tierDraft ? tierDraft.map((t, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="py-1"><Input value={t.name} onChange={(e) => updateTier(i, 'name', e.target.value)} /></td>
                      <td className="py-1">
                        <select value={t.gender} onChange={(e) => updateTier(i, 'gender', e.target.value)} className="w-full px-2 py-1 border border-input rounded-md bg-background text-sm">
                          <option value="">—</option>
                          <option value="female">female</option>
                          <option value="male">male</option>
                        </select>
                      </td>
                      <td className="py-1 w-20"><Input type="number" min="0" value={t.release_step} onChange={(e) => updateTier(i, 'release_step', e.target.value)} /></td>
                      <td className="py-1 w-28"><Input type="number" min="0" value={t.price_cents} onChange={(e) => updateTier(i, 'price_cents', e.target.value)} placeholder="cents" /></td>
                      <td className="py-1 w-24"><Input type="number" min="0" value={t.capacity} onChange={(e) => updateTier(i, 'capacity', e.target.value)} /></td>
                      <td className="py-1 text-right"><button onClick={() => removeTier(i)} className="text-destructive text-sm" title="Remove">×</button></td>
                    </tr>
                  )) : event.tiers.map((t) => (
                    <tr key={t.id} className="border-t border-border">
                      <td className="py-1.5">{t.name}</td>
                      <td className="py-1.5 text-muted-foreground">{t.gender || '—'}</td>
                      <td className="py-1.5 text-muted-foreground">{t.release_step ?? '—'}</td>
                      <td className="py-1.5 text-right"><PriceCents cents={t.price_cents} /></td>
                      <td className="py-1.5 text-right">{t.capacity ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </Section>

          {/* Publish defaults (template-driven batch sync) */}
          {event.template_publish_defaults?.length > 0 && (
            <Section title={`Publish defaults (${event.template_publish_defaults.length} platforms)`}>
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="text-xs text-muted-foreground">
                  Pushes to: {event.template_publish_defaults.map((p) => p.platform).join(', ')}
                </div>
                <button
                  onClick={publishAllDefaults}
                  disabled={publishing}
                  className="px-3 py-1 text-xs bg-primary text-primary-foreground rounded-md hover:opacity-90 disabled:opacity-50"
                >{publishing ? 'Publishing…' : 'Publish all defaults'}</button>
              </div>
              {publishError && <div className="text-xs text-destructive mb-2">Error: {publishError}</div>}
              {publishResults && (
                <>
                {/* Mobile: result cards (the table below is md+ only) */}
                <div className="md:hidden space-y-2 mt-2">
                  <div className="text-xs font-medium">
                    {publishResults.ok} of {publishResults.total} succeeded
                    {publishResults.failed > 0 && <span className="text-destructive ml-2">({publishResults.failed} failed)</span>}
                  </div>
                  {publishResults.results.map((r, i) => (
                    <div key={i} className="border border-border rounded-md p-3 bg-card text-sm">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium">{r.platform}</span>
                        {r.ok ? <span className="text-emerald-600 dark:text-emerald-400">✓</span> : <span className="text-destructive">✗</span>}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {r.skipped ? 'skipped (manual_only)' : r.error || r.status || 'ok'}
                        {r.detail && <span className="block text-[10px] opacity-70">{r.detail}</span>}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border border-border rounded-md overflow-x-auto mt-2 hidden md:block">
                  <div className="px-3 py-1.5 text-xs bg-muted/50 font-medium">
                    {publishResults.ok} of {publishResults.total} succeeded
                    {publishResults.failed > 0 && <span className="text-destructive ml-2">({publishResults.failed} failed)</span>}
                  </div>
                  <table className="w-full text-xs">
                    <tbody>
                      {publishResults.results.map((r, i) => (
                        <tr key={i} className="border-t border-border">
                          <td className="px-3 py-1.5">{r.platform}</td>
                          <td className="px-3 py-1.5 text-center w-6">
                            {r.ok ? <span className="text-emerald-600 dark:text-emerald-400">✓</span> : <span className="text-destructive">✗</span>}
                          </td>
                          <td className="px-3 py-1.5 text-muted-foreground">
                            {r.skipped ? 'skipped (manual_only)' : r.error || r.status || 'ok'}
                            {r.detail && <span className="block text-[10px] opacity-70">{r.detail}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </>
              )}
            </Section>
          )}

          {/* Publications (read-only this chunk) */}
          <Section title={`Publications (${event.publications.length})`}>
            {event.publications.length === 0 ? (
              <div className="text-sm text-muted-foreground">Not published to any platform yet.</div>
            ) : (
              <>
              {/* Mobile: publication cards (the table below is md+ only) */}
              <div className="md:hidden space-y-2">
                {event.publications.map((p) => (
                  <div key={p.id} className="border border-border rounded-md p-3 bg-card text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <PublicationPip pub={p} />
                        <span className="font-medium">{p.platform}{p.platform === event.primary_ticketing_platform && <span className="ml-1 text-xs text-primary">primary</span>}</span>
                      </div>
                      <PublicationSyncButton
                        tenantSlug={tenantSlug}
                        eventId={eventId}
                        publication={p}
                        onDone={refresh}
                      />
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">Status: {p.status}</div>
                    <div className="text-xs text-muted-foreground font-mono mt-0.5">External ID: {p.external_id || '—'}</div>
                    <div className="text-xs mt-0.5">
                      {p.external_url ? (
                        <a href={p.external_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-primary hover:underline break-all">{p.external_url}</a>
                      ) : <span className="text-muted-foreground">No URL</span>}
                    </div>
                  </div>
                ))}
              </div>
              <div className="overflow-x-auto hidden md:block">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="py-1 w-6"></th>
                    <th className="py-1">Platform</th>
                    <th className="py-1">Status</th>
                    <th className="py-1">External ID</th>
                    <th className="py-1">URL</th>
                    <th className="py-1 w-32 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {event.publications.map((p) => (
                    <tr key={p.id} className="border-t border-border">
                      <td className="py-1.5"><PublicationPip pub={p} /></td>
                      <td className="py-1.5">{p.platform}{p.platform === event.primary_ticketing_platform && <span className="ml-1 text-xs text-primary">primary</span>}</td>
                      <td className="py-1.5 text-muted-foreground">{p.status}</td>
                      <td className="py-1.5 text-muted-foreground font-mono text-xs">{p.external_id || '—'}</td>
                      <td className="py-1.5">
                        {p.external_url ? (
                          <a href={p.external_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-primary hover:underline truncate inline-block max-w-xs">{p.external_url}</a>
                        ) : '—'}
                      </td>
                      <td className="py-1.5 text-right">
                        <PublicationSyncButton
                          tenantSlug={tenantSlug}
                          eventId={eventId}
                          publication={p}
                          onDone={refresh}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              </>
            )}
            {event.primary_ticketing_url && (
              <div className="mt-3 text-sm">
                <span className="text-muted-foreground">Primary ticketing URL: </span>
                <a href={event.primary_ticketing_url} target="_blank" rel="noreferrer" className="text-primary hover:underline">{event.primary_ticketing_url}</a>
              </div>
            )}
          </Section>

          {/* Platform sync state, versions, drift, audit log.
              id="audit" is the scroll target for the drawer's
              "Audit history →" link. */}
          <div id="audit">
            <SyncStatePanel event={event} />
          </div>

          {/* Universe, link to the focused override editor.
              The 10-tab in-page editor was retired; UniverseOverrides
              flips the default to "show only what's actually overridden". */}
          <div id="universe-section">
          <Section title="Universe">
            <div className="flex items-center justify-between text-sm">
              <div>
                <div>
                  <span className="font-medium">{Object.keys(event.universe_overrides || {}).length}</span>
                  <span className="text-muted-foreground"> override(s) on this event</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Every other field is inherited from the template + venue defaults.
                </div>
              </div>
              <Link
                to={`/${tenantSlug}/event/${eventId}/universe${event.has_drift ? '#drift' : ''}`}
                className={`px-3 py-1.5 text-sm border rounded-md hover:bg-accent ${event.has_drift ? 'border-amber-500/50 text-amber-700 dark:text-amber-300' : 'border-input'}`}
              >{event.has_drift ? '◆ Resolve Universe drift →' : 'Customize for Universe →'}</Link>
            </div>
          </Section>
          </div>

          {/* Meta performance, hidden unless this event has Meta pushes.
              id="meta" is the scroll target for the drawer's "View
              performance →" link. */}
          <div id="meta">
            <MetaPerformance tenantSlug={tenantSlug} eventId={eventId} />
          </div>

          {/* Description HTML (view-rendered or edit-textarea) */}
          <Section title="Description (basic HTML)">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs text-muted-foreground">
                {editing && draft.description_html
                  ? `${draft.description_html.length} chars`
                  : event.description_html
                  ? `${event.description_html.length} chars`
                  : 'empty'}
              </div>
              <div className="flex items-center gap-2">
                {genError && <span className="text-xs text-destructive">{genError}</span>}
                <button
                  onClick={generate}
                  disabled={generating}
                  className="px-2 py-1 text-xs border border-input rounded-md hover:bg-accent disabled:opacity-50"
                  title="Generate description + short_description via Claude using the tenant's prompt template"
                >{generating ? 'Generating…' : '✨ Generate'}</button>
              </div>
            </div>
            {editing ? (
              <Textarea
                value={draft.description_html}
                onChange={setField('description_html')}
                rows={12}
                placeholder="<b>Talent at Venue</b>&#10;&#10;<b>Event Details:</b>&#10;..."
                className="font-mono text-xs"
              />
            ) : event.description_html && event.description_html.length > 30 ? (
              <div
                className="prose prose-sm max-w-none dark:prose-invert"
                dangerouslySetInnerHTML={{ __html: event.description_html }}
              />
            ) : (
              <div className="text-sm text-muted-foreground">No description.</div>
            )}
          </Section>

          {/* Internal notes */}
          <Section title="Internal notes">
            {editing ? (
              <Textarea value={draft.internal_notes} onChange={setField('internal_notes')} rows={4} />
            ) : event.internal_notes ? (
              <div className="text-sm whitespace-pre-wrap">{event.internal_notes}</div>
            ) : (
              <div className="text-sm text-muted-foreground">—</div>
            )}
          </Section>
        </div>
      </div>

      {drawerOpen && (
        <EventDrawer
          event={event}
          tenantSlug={tenantSlug}
          onClose={() => setDrawerOpen(false)}
          onAction={refresh}
        />
      )}
    </div>
  );
}

function publicationSyncEndpoint(platform) {
  if (platform === 'universe') return '/universe/sync';
  if (platform === 'urvenue')  return '/urvenue/sync';
  if (platform.startsWith('monday_')) return `/monday/${platform.slice('monday_'.length)}`;
  if (['bandsintown', 'resident_advisor', 'jambase'].includes(platform)) return `/distribution/${platform}`;
  return null; // billboard / led / unknown, no API path
}

function PublicationSyncButton({ tenantSlug, eventId, publication, onDone }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const path = publicationSyncEndpoint(publication.platform);
  if (!path) {
    return <span className="text-xs text-muted-foreground" title="No API for this platform">manual only</span>;
  }

  const label = publication.external_id ? 'Sync' : 'Publish';
  const run = async (e) => {
    e.stopPropagation();
    if (publication.external_id && !confirm(`Sync ${publication.platform} for this event?`)) return;
    if (!publication.external_id && !confirm(`Publish to ${publication.platform} for the first time?`)) return;
    setBusy(true);
    setResult(null);
    try {
      const res = await apiFetch(`/api/tenants/${tenantSlug}/events/${eventId}${path}`, { method: 'POST' });
      const d = await res.json();
      if (!res.ok) {
        setResult({ ok: false, error: d.error || `http ${res.status}`, detail: d.detail });
      } else {
        setResult({ ok: !!d.ok, status: d.status });
        if (d.ok) onDone();
      }
    } catch (err) {
      setResult({ ok: false, error: err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col items-end gap-0.5">
      <button
        onClick={run}
        disabled={busy}
        className="px-2 py-0.5 text-xs border border-input rounded-md hover:bg-accent disabled:opacity-50"
      >{busy ? '…' : label}</button>
      {result && (
        <span className={`text-[10px] ${result.ok ? 'text-emerald-600 dark:text-emerald-400' : 'text-destructive'}`}
              title={result.detail || ''}>
          {result.ok ? '✓ ok' : `✗ ${result.error}`}
        </span>
      )}
    </div>
  );
}

function MetaPerformance({ tenantSlug, eventId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setData(null); setError(null);
    apiFetch(`/api/tenants/${tenantSlug}/events/${eventId}/meta-insights`)
      .then((r) => r.json().then((b) => ({ ok: r.ok, body: b })))
      .then(({ ok, body }) => {
        if (!ok) {
          setError(body.error === 'meta_creds_missing' ? null : body.detail || body.error);
          return;
        }
        setData(body);
      })
      .catch((e) => setError(e.message));
  }, [tenantSlug, eventId]);

  if (error === null && !data) return null;
  // Hide entirely if no campaigns matched — keeps the page clean for events
  // without Meta spend (most past LIV events have one matched campaign).
  if (data && data.matched_count === 0) return null;

  const dollars = (c) => `$${(c / 100).toFixed(2)}`;
  const t = data?.totals;

  return (
    <Section title="Meta performance">
      {error && <div className="text-sm text-destructive mb-2">Error: {error}</div>}
      {data && (
        <>
          <div className="mb-3 flex items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>
              {data.matched_count} matching campaign{data.matched_count === 1 ? '' : 's'} (matched by date in campaign name)
            </span>
            <FreshnessBadge cacheMeta={data.cache_meta} />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm mb-4">
            <MetricCard label="Spend"      value={dollars(t.spend_cents)} tone="red" />
            <MetricCard label="Impressions" value={t.impressions.toLocaleString()} tone="blue" />
            <MetricCard label="Reach"      value={t.reach.toLocaleString()} tone="indigo" />
            <MetricCard label="Clicks"     value={t.clicks.toLocaleString()} tone="blue" />
            <MetricCard label="CTR"        value={`${t.ctr.toFixed(2)}%`} />
            <MetricCard label="CPM"        value={dollars(Math.round(t.cpm * 100))} />
            <MetricCard label="CPC"        value={dollars(Math.round(t.cpc * 100))} />
            <MetricCard label="Frequency"  value={t.frequency.toFixed(2)} />
            <MetricCard label="Purchases"  value={t.purchases.toLocaleString()} tone="green" />
            <MetricCard label="Purchase value" value={dollars(t.purchase_value_cents)} tone="green" />
            <MetricCard label="Cost / purchase" value={t.cost_per_purchase_cents > 0 ? dollars(t.cost_per_purchase_cents) : '—'} />
            <MetricCard label="ROAS"       value={t.spend_cents > 0 ? `${t.roas.toFixed(2)}x` : '—'} tone="green" />
          </div>

          {data.campaigns.length > 1 && (
            <>
            {/* Mobile: per-campaign cards (the table below is md+ only) */}
            <div className="md:hidden space-y-2">
              {data.campaigns.map((c) => (
                <div key={c.id} className="border border-border rounded-md p-3 bg-card text-sm">
                  <div className="font-medium truncate">{c.name}</div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground mt-1 tabular-nums">
                    <span>Adsets: {c.adsets_count}</span>
                    <span>Spend: {dollars(c.totals.spend_cents)}</span>
                    <span>Purchases: {c.totals.purchases.toLocaleString()}</span>
                    <span>ROAS: {c.totals.spend_cents > 0 ? `${c.totals.roas.toFixed(2)}x` : '—'}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="border border-border rounded-md overflow-x-auto hidden md:block">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 text-left">
                  <tr>
                    <th className="px-3 py-2 font-medium">Campaign</th>
                    <th className="px-3 py-2 font-medium text-right">Adsets</th>
                    <th className="px-3 py-2 font-medium text-right">Spend</th>
                    <th className="px-3 py-2 font-medium text-right">Purchases</th>
                    <th className="px-3 py-2 font-medium text-right">ROAS</th>
                  </tr>
                </thead>
                <tbody>
                  {data.campaigns.map((c) => (
                    <tr key={c.id} className="border-t border-border">
                      <td className="px-3 py-1.5 truncate max-w-xs">{c.name}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{c.adsets_count}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{dollars(c.totals.spend_cents)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{c.totals.purchases.toLocaleString()}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{c.totals.spend_cents > 0 ? `${c.totals.roas.toFixed(2)}x` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </>
      )}
    </Section>
  );
}

function MetricCard({ label, value, tone }) {
  const toneClass = {
    red:    'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300',
    blue:   'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300',
    green:  'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300',
    indigo: 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300',
  }[tone] || 'bg-card';
  return (
    <div className={`border border-border rounded-md p-2 ${toneClass}`}>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-base font-semibold tabular-nums">{value}</div>
    </div>
  );
}

// Secondary button: pushes current canonical state to platforms (UrVenue
// inventory + day templates, etc.) WITHOUT flipping status to announced.
// Used for placeholder pushes, post-edit updates, and cancel reverts.
// Derive the platform chip — pulls from event.publications first, falls
// back to the template's publish_defaults so a freshly-created event with
// no pubs yet still shows where the next sync will go.
function platformChip(event) {
  const fromPubs = (event.publications || []).map((p) => p.platform);
  const fromDefaults = (event.template_publish_defaults || []).map((p) => p.platform).filter(Boolean);
  const seen = new Set();
  const list = [...fromPubs, ...fromDefaults].filter((p) => !seen.has(p) && seen.add(p));
  return list.length ? list.join(' · ') : 'no platforms configured';
}

function SyncPlatformsButton({ tenantSlug, eventId, event, onSynced }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  if (event.status === 'cancelled') return null;
  const platforms = platformChip(event);

  const click = async () => {
    if (!confirm(`Sync canonical state to: ${platforms}\n\nStatus will NOT change.`)) return;
    setBusy(true); setErr(null);
    try {
      const res = await apiFetch(`/api/tenants/${tenantSlug}/events/${eventId}/sync-platforms?authorize_writes=1`, { method: 'POST' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || d.error || 'sync failed');
      onSynced();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col items-end">
      <div className="flex items-center gap-1.5">
        {err && <span className="text-xs text-destructive">{err}</span>}
        <button
          onClick={click}
          disabled={busy}
          title={`Sync to: ${platforms}. Status unchanged.`}
          className="px-3 py-1.5 text-sm border border-input rounded-md hover:bg-accent disabled:opacity-50"
        >{busy ? 'Syncing…' : 'Sync to platforms'}</button>
      </div>
      <span className="text-[10px] text-muted-foreground mt-0.5">→ {platforms} · no status change</span>
    </div>
  );
}

function AnnounceButton({ tenantSlug, eventId, event, onAnnounced }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Don't show for already-announced or cancelled events.
  if (event.status === 'announced' || event.status === 'cancelled') return null;

  // Talent gate only. The flyer gate dropped — every template ships with a
  // default flyer; resolver/publisher fall back to it automatically when no
  // event-level flyer is set, so blocking on it just confuses ops.
  const isPlaceholderTalent = !event.talent || /^(tbd|tba|placeholder|artist\s*\d+)$/i.test(event.talent.trim());
  const disabled = isPlaceholderTalent;
  const platforms = platformChip(event);
  const tooltip = isPlaceholderTalent
    ? 'Set a real talent name before announcing'
    : `Sets status to Live and fans out to: ${platforms}`;

  const click = async () => {
    if (disabled) return;
    setBusy(true); setErr(null);
    try {
      const res = await apiFetch(`/api/tenants/${tenantSlug}/events/${eventId}/announce?authorize_writes=1`, { method: 'POST' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || d.error || 'announce failed');
      onAnnounced();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col items-end">
      <div className="flex items-center gap-1.5">
        {err && <span className="text-xs text-destructive">{err}</span>}
        <button
          onClick={click}
          disabled={disabled || busy}
          title={tooltip}
          className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
        >{busy ? 'Announcing…' : 'Announce + sync'}</button>
      </div>
      <span className="text-[10px] text-muted-foreground mt-0.5">→ {platforms} · sets status to Live</span>
    </div>
  );
}

// Sync state, version history, and edit audit log. Renders a per-platform
// summary plus a chronological history of pushes interleaved with field
// changes. State labels are derived (no DB column) from the three
// snapshot comparisons documented in the plan.
function SyncStatePanel({ event }) {
  const tz = useVenueTz();
  const [showAudit, setShowAudit] = useState(false);
  const versions = event.versions || [];
  const sync = event.platform_sync || [];
  const audit = event.audit_log || [];

  return (
    <Section title="Sync state">
      {sync.length === 0 ? (
        <div className="text-sm text-muted-foreground">
          ○ Not pushed to any platform yet. Announce this event to push to all platforms.
        </div>
      ) : (
        <div className="space-y-2">
          {sync.map((s) => (
            <PlatformSyncCard key={s.platform} sync={s} versions={versions} />
          ))}
        </div>
      )}

      {versions.length > 0 && (
        <div className="mt-3 text-xs text-muted-foreground">
          Versions: {versions.length} (latest v{versions[0].version_number} · {versions[0].kind} · {formatStamp(versions[0].created_at, tz)})
        </div>
      )}

      <div className="mt-3 flex items-center justify-between">
        <button onClick={() => setShowAudit((s) => !s)} aria-expanded={showAudit} className="text-xs text-primary hover:underline">
          {showAudit ? 'Hide' : 'Show'} edit history ({audit.length})
        </button>
      </div>

      {showAudit && audit.length > 0 && (
        <>
        {/* Mobile: audit entry cards (the table below is md+ only) */}
        <div className="md:hidden mt-2 space-y-2 text-xs">
          {audit.map((a) => (
            <div key={a.id} className="border border-border rounded-md p-3 bg-card">
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">{formatStamp(a.changed_at, tz)}</span>
                <span className="text-muted-foreground font-mono">{a.source}</span>
              </div>
              <div className="mt-1">
                {Object.entries(a.changed_fields).map(([field, diff]) => (
                  <div key={field}>
                    <span className="font-medium">{field}</span>
                    <span className="text-muted-foreground"> · {JSON.stringify(diff.from) || 'null'} → {JSON.stringify(diff.to) || 'null'}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-2 border border-border rounded-md overflow-x-auto text-xs hidden md:block">
          <table className="w-full">
            <thead className="bg-muted/40 text-left text-muted-foreground">
              <tr>
                <th className="px-2 py-1">When</th>
                <th className="px-2 py-1">Source</th>
                <th className="px-2 py-1">Field changes</th>
              </tr>
            </thead>
            <tbody>
              {audit.map((a) => (
                <tr key={a.id} className="border-t border-border align-top">
                  <td className="px-2 py-1 whitespace-nowrap text-muted-foreground">{formatStamp(a.changed_at, tz)}</td>
                  <td className="px-2 py-1 text-muted-foreground font-mono">{a.source}</td>
                  <td className="px-2 py-1">
                    {Object.entries(a.changed_fields).map(([field, diff]) => (
                      <div key={field}>
                        <span className="font-medium">{field}</span>
                        <span className="text-muted-foreground"> · {JSON.stringify(diff.from) || 'null'} → {JSON.stringify(diff.to) || 'null'}</span>
                      </div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
    </Section>
  );
}

function PlatformSyncCard({ sync, versions }) {
  const tz = useVenueTz();
  const live = versions.find((v) => v.id === sync.live_version_id);
  const tone =
    !sync.live_version_id ? 'bg-muted text-muted-foreground' :
    sync.drift_resolved_at === null && sync.last_pulled_at ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300' :
    'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300';
  const stateLabel =
    !sync.live_version_id ? '○ Not pushed' :
    sync.drift_resolved_at === null && sync.last_pulled_at ? '◆ Possible drift' :
    '✓ Synced';

  return (
    <div className="border border-border rounded-md p-3 bg-card">
      <div className="flex items-center justify-between">
        <div>
          <span className="font-medium">{sync.platform}</span>
          {sync.external_id && <span className="ml-2 text-xs text-muted-foreground font-mono">id: {sync.external_id}</span>}
        </div>
        <span className={`text-xs px-2 py-0.5 rounded ${tone}`}>{stateLabel}</span>
      </div>
      <div className="mt-1 text-xs text-muted-foreground space-y-0.5">
        {live && (
          <div>live: v{live.version_number} <span className="opacity-70">({live.kind})</span> · pushed {formatStamp(sync.last_pushed_at, tz)}</div>
        )}
        {sync.last_pulled_at && (
          <div>last pulled: {formatStamp(sync.last_pulled_at, tz)}</div>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-2">{title}</h2>
      <div className="border border-border rounded-md p-3 bg-card">{children}</div>
    </div>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3 text-sm">{children}</div>;
}

function Field({ label, children, overridden }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground mb-0.5 flex items-center gap-1">
        {label}
        {overridden && (
          <span
            className="text-[10px] px-1 py-0 rounded bg-amber-500/10 text-amber-700 dark:text-amber-300"
            title="Manually edited, cron mirror won't overwrite this"
          >edited</span>
        )}
      </div>
      <div>{children}</div>
    </div>
  );
}

function Input({ className = '', ...props }) {
  return (
    <input
      {...props}
      className={`w-full px-2 py-1 border border-input rounded-md bg-background ${className}`}
    />
  );
}

function Textarea({ className = '', ...props }) {
  return (
    <textarea
      {...props}
      className={`w-full px-2 py-1 border border-input rounded-md bg-background ${className}`}
    />
  );
}

function MediaSlot({ label, kind, url, accept, aspect = 'aspect-square', uploading, uploadError, onUpload }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs font-medium text-muted-foreground">{label}</div>
        <label className="text-xs text-primary cursor-pointer hover:underline">
          {uploading ? 'Uploading…' : url ? 'Replace…' : 'Upload…'}
          <input
            type="file"
            accept={accept}
            className="hidden"
            disabled={uploading}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onUpload(f);
              e.target.value = '';
            }}
          />
        </label>
      </div>
      {uploadError && <div className="text-xs text-destructive mb-1">{uploadError}</div>}
      {url ? (
        <img src={url} alt="" className={`w-full rounded-md bg-muted ${aspect} object-cover`} />
      ) : (
        <div className={`w-full ${aspect} rounded-md bg-muted flex items-center justify-center text-xs text-muted-foreground`}>
          no {kind}
        </div>
      )}
    </div>
  );
}

function AdditionalMediaGallery({ media, uploading, onUpload, onDelete }) {
  // Anything that ISN'T a primary flyer/banner/cover image.
  const PRIMARY_IMAGE_KINDS = new Set(['flyer', 'banner', 'cover']);
  const extras = (media || []).filter(
    (m) => !(m.media_type === 'image' && m.is_primary && PRIMARY_IMAGE_KINDS.has(m.kind)),
  );
  const [showAdd, setShowAdd] = useState(false);
  const [newKind, setNewKind] = useState('promo_poster');
  const [newMediaType, setNewMediaType] = useState('image');

  return (
    <div className="border-t border-border pt-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-xs font-medium text-muted-foreground">Additional media ({extras.length})</div>
        <button
          onClick={() => setShowAdd((s) => !s)}
          className="text-xs text-primary hover:underline"
        >{showAdd ? 'Cancel' : '+ Add'}</button>
      </div>
      {showAdd && (
        <div className="border border-input rounded-md p-2 space-y-2 bg-muted/30">
          <div className="flex gap-2">
            <select
              value={newMediaType}
              onChange={(e) => setNewMediaType(e.target.value)}
              className="text-xs px-2 py-1 border border-input rounded-md bg-background"
            >
              <option value="image">Image</option>
              <option value="video">Video</option>
            </select>
            <input
              type="text"
              value={newKind}
              onChange={(e) => setNewKind(e.target.value)}
              placeholder="kind (e.g. promo_poster, reel, recap)"
              className="flex-1 text-xs px-2 py-1 border border-input rounded-md bg-background"
            />
          </div>
          <label className="block text-xs text-primary cursor-pointer hover:underline">
            {uploading ? 'Uploading…' : 'Choose file…'}
            <input
              type="file"
              accept={newMediaType === 'video' ? 'video/mp4,video/quicktime,video/webm' : 'image/jpeg,image/png,image/webp,image/gif'}
              className="hidden"
              disabled={uploading || !newKind.trim()}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) {
                  onUpload(f, newKind.trim());
                  setShowAdd(false);
                }
                e.target.value = '';
              }}
            />
          </label>
        </div>
      )}
      {extras.length === 0 ? (
        <div className="text-xs text-muted-foreground italic">No additional media.</div>
      ) : (
        <div className="space-y-2">
          {extras.map((m) => (
            <div key={m.id} className="border border-border rounded-md overflow-hidden bg-background">
              {m.media_type === 'video' ? (
                <video src={m.url} controls poster={m.poster_url || undefined} className="w-full" />
              ) : (
                <img src={m.url} alt="" className="w-full" />
              )}
              <div className="flex items-center justify-between px-2 py-1 text-[10px]">
                <span className="text-muted-foreground">
                  {m.media_type} · {m.kind}
                  {m.duration_seconds ? ` · ${Math.round(m.duration_seconds)}s` : ''}
                  {m.source ? ` · ${m.source}` : ''}
                </span>
                <button
                  onClick={() => onDelete(m.id)}
                  className="text-destructive hover:underline"
                >Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

